import React, { FC, useEffect, useState } from 'react';

import { setAuthToken } from 'core/Api';
import { getAccessToken } from 'utils/auth';
import AppLoading from 'components/AppLoading';
import ModalComponent from 'components/Modal';
import { PermissionsProvider } from './permissions';
import { WizardProvider } from './wizard';
import { ActiveClientProvider } from './active-client';
import { LanguageProvider } from './language';
import { SelectableTableProvider } from './selectable-table';
import { ModalProvider } from './modal';
import { WorkModeProvider } from './work-mode';
import { TwoFactorProvider } from './two-factor';

import 'core/i18n';

interface IProps {
  children: React.ReactNode;
}

const GlobalStoreProvider: FC<IProps> = ({ children }: IProps) => {
  const [isLoading, setIsLoading] = useState(true);
  /**
   * Initial Authorization header setup
   *
   * Get stored locally access token and set it
   * as Authorization header if it exists in store
   *
   * Use `isLoading` mechanism to make sure that the
   * token is set before making any requests to the API
   * This use case is possible if user is logged in and
   * the access token is set but page has been refreshed
   * so the API module doesn't have the Auth header set yet
   */
  useEffect(() => {
    const accessToken = getAccessToken();
    if (accessToken) setAuthToken(accessToken);
    setIsLoading(false);
  }, []);

  if (isLoading) return <AppLoading />;

  return (
    <PermissionsProvider>
      <TwoFactorProvider>
        <ActiveClientProvider>
          <WorkModeProvider>
            <LanguageProvider>
              <WizardProvider>
                <SelectableTableProvider>
                  <ModalProvider>
                    <ModalComponent />
                    {children}
                  </ModalProvider>
                </SelectableTableProvider>
              </WizardProvider>
            </LanguageProvider>
          </WorkModeProvider>
        </ActiveClientProvider>
      </TwoFactorProvider>
    </PermissionsProvider>
  );
};

export default GlobalStoreProvider;
